import BaseService from '../../base/base_service';
import { SYSTEM_EVENTS } from '../../events/events';
import Device from '../../utils/device/device';
import _ from '../../utils/underscore';
import {QA_ENV_URBAN_DEFAULT_SETTINGS} from "../../../../common/const/service-worker-constants";
import IWebPushService from "./web_push_service_interface";
import {isOrbitInQAMode} from "../../../../utils/qa-mode-utils";

export interface IUrbanAirshipData {
    defaultIcon: string;
    defaultTitle: string;
    defaultActionURL: string;
    appKey: string;
    token: string;
    vapidPublicKey: string;
}

export interface IWebPushParams {
    enabled: boolean;
    urbanAirshipSdkUrl: string;
    prodSettings: IUrbanAirshipData;
    loadWebPush: () => Promise<void>;
    testSettings?: IUrbanAirshipData;
    uaSdkLoadTestPromise?: (uaSdkLoadPromise: Promise<void>) => void;
}

export interface IUASdk {
    register: () => Promise<any>;
    canRegister:boolean;
    getChannel: () => Promise<{
        namedUser: {
            set: (paramName: string) => void;
            remove: (paramName: string) => void;
        };

        tags: {
            add: (paramName: string) => void;
            remove: (paramName: string) => void;
        }
    }>
}

const LOAD_UA_SDK_INTERVAL: number = 40000;

export default class WebPushService extends BaseService implements IWebPushService{
    static CLASS = 'WebPushService';

    private _initParams: IWebPushParams;
    private _swUrl:string;
    private _loadUAScriptObservable;

    private _uaSdkLoadPromise: Promise<void> = null;
    private _uaSdkLoadPromiseResolve = null;
    private _uaSdkLoadPromiseReject = null;

    init(params: IWebPushParams): void {
        super.init(params);

        if (!params.enabled || Device.hybrid.isHybrid || Device.os.isIOS){
            return;
        }

        if(_.isNullOrUndefined(params.urbanAirshipSdkUrl)) {
            this.throwError('64',`${this.getClassName()} expects parameter urbanAirshipSdkUrl which was not supplied`, {params});
        }

        this._initParams = params;

        this._uaSdkLoadPromise = new Promise( (resolve,reject) => {
            this._uaSdkLoadPromiseResolve = () => {
                this._clearTimeout();
                resolve();
            }
            this._uaSdkLoadPromiseReject = reject;
        });

        this._uaSdkLoadPromise
            .then(() => this._setChannelInfo())
            .catch( err => {
                    this._sendElasticData('Web push fatal error: Airship SDK load error:' + (err && err.message), true);
            })

        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, () => this._onInitComplete());
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => this._uaSdkLoadPromise.then( () => this._setChannelInfo()));

        // used for unit tests
        if (_.isFunction(params.loadWebPush)) {
            this._loadWebPush = params.loadWebPush;
        }
        if(_.isFunction(params.uaSdkLoadTestPromise)){
            params.uaSdkLoadTestPromise(this._uaSdkLoadPromise);
        }
        //
    }

    private _onInitComplete(){
        const getServiceWorkerUrl = this.serviceFactory.windowService().getGlobalProperty('getOrbitServiceWorkerUrl');
        if (_.isFunction(getServiceWorkerUrl)) {
            this._swUrl = getServiceWorkerUrl();
        } else {
            this._sendElasticData('Web push fatal error: getOrbitServiceWorkerUrl does not exist in window, service-worker.js was not loaded', true);
            return;
        }

        this._startTimeout();
    }

    private _startTimeout(){
        this._loadUAScriptObservable = this.serviceFactory.observableFactoryService().fromTimeout(LOAD_UA_SDK_INTERVAL)
            .forEach(() => {
                this._loadWebPush();
            })
    }

    private _getSDK(): PromiseLike<IUASdk> {
        const UA = this.serviceFactory.windowService().getGlobalProperty('UA');

        UA.catch(() => {
            this._sendElasticData('Error Loading UA SDK object', true);
        });

        return UA;
    }

    private _registerToUA(): void {
        this._uaSdkLoadPromise && this._uaSdkLoadPromise.then(() => {
            return this._getSDK().then( (SDK: IUASdk) => {
                if ("Notification" in window && Notification.permission === "denied") {
                    this.logger.warn('User chose to deny push notifications');
                }

                if (SDK.canRegister) {
                    //we check if window.Notification exists and notification is default/ask
                    const shouldSendAnalytics = "Notification" in window && Notification.permission === "default";

                    if (shouldSendAnalytics) {
                        this._sendAnalytics("Prompt");
                    }

                    //since UA register API is the one that actually call Notification.requestPermission()
                    //we must rely on that
                    //this fragment below is from Urban Airship v1 source code - https://aswpsdkus.com/notify/v1/ua-sdk.js
                    /*
                    export default function getRegistration(
                        workerRegistration: ServiceWorkerRegistration | null
                      ): Promise<ChannelRegistration> {
                        const showPerm = (
                          swRegistration: ServiceWorkerRegistration
                        ): Promise<ServiceWorkerRegistration> => {
                          return Notification.requestPermission().then(
                            (perm: NotificationPermission) => {
                              if (perm === 'granted') {
                                return swRegistration
                              }
                              throw new Error('Notification permission is denied.')
                            }
                          )
                        }
                      
                        if (workerRegistration) {
                          return showPerm(workerRegistration)
                            .then(getVapidSubscription)
                            .then((sub) => {
                              return LocaleOverride.fromStorage().then((loc) => {
                                const regBase = getRegistrationBase(loc)
                                return getVapidData(sub, regBase)
                              })
                            })
                        }
                      
                        return Promise.reject('Worker registration failed')
                      }
                    */
                      
                    SDK.register()
                        .then(() => {
                            if (shouldSendAnalytics) {
                                this._checkNotificationChange();
                            }
                        })
                        .catch(() => {
                            if (shouldSendAnalytics) {
                                this._checkNotificationChange();
                            }
                        });
                }
            })
        });
    }

    _checkNotificationChange() {
        if (Notification.permission === "granted") {
            this._sendAnalytics("ALLOW");
        } else {
            if (Notification.permission !== "default") {
                this._sendAnalytics("BLOCK");
            }
        }
    }

    private _sendAnalytics(action: string) {
        const cid = this.serviceFactory.userService().getCID();
        this.serviceFactory.analyticsService().sendEvent({
            category: 'Push Notification',
            action: action,
            label: cid
        });
    }

    private async _setChannelInfo() {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) { // are we sure we don't have to do anything when the user logs out?
            return;
        }

        this._getSDK().then( async (SDK: IUASdk) => {
            const channel = await SDK.getChannel();

            const cid = this.serviceFactory.userService().getCID();
            channel.namedUser.set(String(cid));
            channel.tags.add(`CID-${String(cid)}`);

            const lang = this.serviceFactory.languageService().getISO3();
            channel.tags.add(`Language-${lang}`);

            const b2cDataString = this.serviceFactory.oAuth2Service().extractTokenProperty('b2c');
            const b2cData = b2cDataString ? JSON.parse(b2cDataString) : null;

            if (b2cData && b2cData.sessionParamsMsg) {

                const spm = b2cData.sessionParamsMsg;

                channel.tags.add(`RegistrationCountry-${spm.RegistrationCountry}`);
                channel.tags.add(`PlayerStatus-${spm.PlayerStatus}`);
                channel.tags.add(`CStatus-${spm.IsFTD ? 'ftd' : 'reg'}`);
            }
            this._sendElasticData(`Web push registered for CID: ${cid}`);
        });
    }

    private _getUASettings():IUrbanAirshipData{
        const isQAModeEnabledFromWindow = isOrbitInQAMode();
        const isQAEnv = isQAModeEnabledFromWindow ? isQAModeEnabledFromWindow : false;
        if (isQAEnv){
            return this._initParams.testSettings ? this._initParams.testSettings : QA_ENV_URBAN_DEFAULT_SETTINGS;
        } else {
            return this._initParams.prodSettings
        }
    }

    private _getUrbanAppDetails(): Partial<IUrbanAirshipData> & {workerUrl: string} {
        const settings = this._getUASettings();
        return {
            workerUrl: this._swUrl,
            vapidPublicKey: settings?.vapidPublicKey || '',
            appKey: settings?.appKey || '',
            token: settings?.token || ''
        };
    }

    _clearTimeout() {
        if (this._loadUAScriptObservable) {
            this._loadUAScriptObservable.dispose();
            this._loadUAScriptObservable = null;
        }
    }

    private _sendElasticData(logText: string, isError:boolean = false): void {
        const analyticsData: any = {
            CGPEventCategory: WebPushService.CLASS,
            CGPEventAction: logText
        };
        if(isError){
            this.serviceFactory.structuredLoggingService().getLogger(analyticsData).error();
        } else {
            this.serviceFactory.structuredLoggingService().getLogger(analyticsData).info();
        }
    }

    private _uaSDKOnLoad(){
        this._uaSdkLoadPromiseResolve();
    }

    // tslint:disable
    /**
     * @param {} n - window
     * @param {} t - document
     * @param {} c - urban sdk
     * @param {} e -'UA'
     * @param {} u - urban app details
     * @returns {}
     */
    private _loadWebPush = _.once(async () => {
        if(!this._initParams || !this._initParams.urbanAirshipSdkUrl){
            return;
        }

        let n = window,
            t = document,
            c = this._initParams.urbanAirshipSdkUrl,
            e = 'UA',
            u = this._getUrbanAppDetails();

        this._clearTimeout();

        const waitForScriptLoad = new Promise(resolve => {
            function r(n){ try{ f=n(u) } catch(n){ return (h=n,void i(p,n)) } i(s,f) }
            function i(n,t){ for(let c=0;c<n.length;c++)d(n[c],t); }
            function o(n,t){ return (n&&(f?d(n,f):s.push(n)),t&&(h?d(t,h):p.push(t)),l); }
            function a(n){return o(!1,n) }
            function d(t,c){ n.setTimeout(function(){t(c)},0)}

            var f,h,s=[],p=[],l={then:o,catch:a,_setup:r};n[e]=l;

            var v: any=t.createElement("script");
            v.src=c;v.onload=resolve;v.async=!0;v.id="_uasdk";v.rel=e;t.head.appendChild(v);
        });

        try {
            await waitForScriptLoad;
        } catch (err) {
            this.logger.error('Failed to load web push', err);
        }


        this._uaSDKOnLoad();
    });
    // tslint:enable

    public promptPermission() {
        this._loadWebPush();
        this._registerToUA();
    }
}
